// InitialAssessmentSummary.tsx
import React from 'react';
import { SeverityLevel } from '../utils/asthmaTypes';
// import './InitialAssessmentSummary.scss'; // Not needed if it is in AsthmaFlow.scss
import stethoscopeIcon from '../icons/stethoscope.svg';
import lungsIcon from '../icons/lungs.svg';
import dyspneaIcon from '../icons/dyspnea.svg';

interface InitialAssessmentSummaryProps {
  age: string;
  respiratoryRate: string;
  oxygenSaturation: string;
  oxygenSupport: boolean;
  auscultation: string;
  retractions: string;
  dyspnea: string;
  score: number;
  severityLabel: string | null;
  activeSeverity: SeverityLevel | null;
  initialAssessmentTime: string | null;
  isModal?: boolean;
}

const getSeverityRangeText = (activeSeverity: SeverityLevel | null) => {
  switch (activeSeverity) {
    case 'MILD': return '(5–7)';
    case 'MODERATE': return '(8–11)';
    case 'SEVERE': return '(12–15)';
    default: return '';
  }
};

const getSeverityClass = (activeSeverity: SeverityLevel | null) => {
  switch (activeSeverity) {
    case 'MILD': return 'bg-green-50';
    case 'MODERATE': return 'bg-yellow-50';
    case 'SEVERE': return 'bg-pink-50';
    default: return 'bg-gray-50';
  }
};

export const InitialAssessmentSummary: React.FC<InitialAssessmentSummaryProps> = ({
  age,
  respiratoryRate,
  oxygenSaturation,
  oxygenSupport,
  auscultation,
  retractions,
  dyspnea,
  score,
  severityLabel,
  activeSeverity,
  initialAssessmentTime,
  isModal = false,
}) => {
  const severityRange = getSeverityRangeText(activeSeverity);
  const severityClass = getSeverityClass(activeSeverity);

  return (
    <div className={`initial-assessment-card ${isModal ? 'modal-view' : ''}`}>

      {!isModal && (
        <div className="initial-assessment-severity-row">
          <div className="initial-assessment-severity-text">
            {severityLabel
              ? `התקף ${severityLabel} – ${score} נקודות ${severityRange}`
              : 'חומרת התקף טרם נקבעה'}
          </div>
        </div>
      )}

      {isModal && <div className="initial-assessment-header">מדדים התחלתיים</div>}

      <div className="initial-assessment-time">
        {initialAssessmentTime
          ? `תחילת מעקב: ${initialAssessmentTime}`
          : 'הערכה טרם הושלמה'}
      </div>

      <div className="initial-assessment-fields">

        <div className="initial-assessment-field">
          <span className="field-label">גיל:</span>
          <span className="field-value">{age || 'לא הוזן'}</span>
        </div>

        <div className="initial-assessment-field">
          <span className="field-label">קצב נשימה (RR):</span>
          <span className="field-value">{respiratoryRate || 'לא הוזן'}</span>
        </div>

        <div className="initial-assessment-field">
          <span className="field-label">רווי חמצן:</span>
          <span className="field-value">
            {oxygenSaturation ? `${oxygenSaturation}%` : 'לא הוזן'}
          </span>
        </div>

        <div className="initial-assessment-field">
          <span className="field-label">תוספת חמצן:</span>
          <span className="field-value">
            {oxygenSupport ? 'ניתנה' : 'לא ניתנה'}
          </span>
        </div>

        {/* --- Auscultation (icon to the right of text) --- */}
        <div className="initial-assessment-field with-icon">
          <div className="field-label-wrapper">
            {/* 1. Placing image first to be on the right side (RTL) */}
            <img src={stethoscopeIcon} alt="icon" className="field-icon" />
            {/* 2. Text appears after it (to the left of icon) */}
            <span className="field-label">האזנה:</span>
          </div>
          <span className="field-value">
            {auscultation === 'mild' && 'צפצופים סוף אקספיריום / בועיות'}
            {auscultation === 'moderate' && 'צפצופים אקספיריום מפושטים'}
            {auscultation === 'severe' && 'צפצופים אינספ+אקספ, כניסת אוויר מופחתת'}
            {!auscultation && 'לא הוזן'}
          </span>
        </div>

        {/* --- Retractions (icon to the right of text) --- */}
        <div className="initial-assessment-field with-icon">
          <div className="field-label-wrapper">
            <img src={lungsIcon} alt="icon" className="field-icon" />
            <span className="field-label">רתיעות:</span>
          </div>
          <span className="field-value">
            {retractions === 'none' && 'ללא / בין צלעיות'}
            {retractions === 'moderate' && 'בין ותת צלעיות'}
            {retractions === 'severe' && 'בין ותת צלעיות + מעל עצמות הבריח'}
            {!retractions && 'לא הוזן'}
          </span>
        </div>

        {/* --- Dyspnea (icon to the right of text) --- */}
        <div className="initial-assessment-field with-icon">
          <div className="field-label-wrapper">
            <img src={dyspneaIcon} alt="icon" className="field-icon" />
            <span className="field-label">דיספנאה / יכולת דיבור:</span>
          </div>
          <span className="field-value">
            {dyspnea === 'mild' && 'אוכל/משחק ללא קושי, דיבור במשפטים'}
            {dyspnea === 'moderate' && 'אוכל בהפסקות, דיבור במשפטים חלקיים'}
            {dyspnea === 'severe' && 'לא יכול לאכול/לשחק, דיבור במילים בודדות'}
            {!dyspnea && 'לא הוזן'}
          </span>
        </div>

      </div>

      {isModal && (
        <div className="summary-severity-wrapper">
          <div className="summary-severity-label">סיכום הערכה ראשונית:</div>
          <div className={`summary-severity-badge ${severityClass}`}>
            {severityLabel
              ? `התקף ${severityLabel} – ${score} נקודות ${severityRange}`
              : 'חומרת התקף טרם נקבעה'}
          </div>
        </div>
      )}

    </div>
  );
};